import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // Important for cookies if you use them
});

// ── Request interceptor: attach JWT ─────────────────────────
// Use sessionStorage so each browser tab has its own isolated
// session (prevents cross-tab account mixup).
const storage = globalThis.sessionStorage;
api.interceptors.request.use((config) => {
  const token = storage.getItem('getnextern_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: handle auth errors ────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const msg = error.response?.data?.error || '';
      if (msg.includes('Session expired') || msg.includes('Invalid token') || msg.includes('Session revoked')) {
        storage.removeItem('getnextern_token');
        globalThis.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ────────────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resendOTP: (data) => api.post('/auth/resend-otp', data),
  login: (data) => api.post('/auth/login', data),
  // B-07: logout must call the backend so the JWT is blacklisted server-side
  logout: () => api.post('/auth/logout'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  uploadProfileImage: (formData) => api.put('/auth/profile-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  changePassword: (data) => api.put('/auth/change-password', data),

  verify2fa:       (data) => api.post('/auth/2fa/login', data),
  setup2fa:        ()     => api.post('/auth/2fa/setup'),
  confirm2fa:      (data) => api.post('/auth/2fa/verify-setup', data),
  disable2fa:      (data) => api.post('/auth/2fa/disable', data),
  recoverySend2fa: (data) => api.post('/auth/2fa/recovery-send', data),
  recoveryVerify2fa: (data) => api.post('/auth/2fa/recovery-verify', data),
};

// ─── Payments ─────────────────────────────────────────────────
export const paymentAPI = {
  createOrder: (data) => api.post('/payments/create-order', data),
  verify: (data) => api.post('/payments/verify', data),
  getHistory: () => api.get('/payments/history'),
  getReceipt: (id) => api.get(`/payments/receipt/${id}`),
};

// ─── Student / Platform ───────────────────────────────────────
export const studentAPI = {
  getDashboard: () => api.get('/student/dashboard'),
  getInternships: (params) => api.get('/student/internships', { params }),
  getInternship: (slug) => api.get(`/student/internships/${slug}`),
  getCurriculum: (slug) => api.get(`/student/internships/${slug}/curriculum`),
  getPreviewLesson: (slug, lessonId) => api.get(`/student/internships/${slug}/curriculum/preview/${lessonId}`),
  getEnrollmentContent: (internshipId) => api.get(`/student/enrollments/${internshipId}/content`),
  getCertificates: () => api.get('/student/certificates'),
  downloadCertificate: (certId) =>
    api.get(`/student/certificates/${certId}/download`, { responseType: 'blob' }),
  downloadCertificateImage: (certId) =>
    api.get(`/student/certificates/${certId}/image`, { responseType: 'blob' }),
  downloadCertificateSvg: (certId) =>
    api.get(`/student/certificates/${certId}/svg`, { responseType: 'blob' }),
  verifyCertificate: (certId) => api.get(`/student/verify/${certId}`),

  // Offer Letters
  getOfferLetters: () => api.get('/offer-letters/my'),
  downloadOfferLetter: (offerLetterId) =>
    api.get(`/offer-letters/download/${offerLetterId}`, { responseType: 'blob' }),
  getNotifications: () => api.get('/student/notifications'),
  getUnreadNotificationCount: () => api.get('/student/notifications/unread-count'),

  // Learning API (Modules & Lessons)
  getModules: (internshipId) => api.get(`/student/enrollments/${internshipId}/modules`),
  getEnrollmentDetails: () => api.get('/student/enrollments/details'),
  getEnrollmentProjects: () => api.get('/student/enrollments/projects'),
  completeLesson: (internshipId, lessonId) =>
    api.post(`/student/enrollments/${internshipId}/lessons/${lessonId}/complete`),
  getProgress: (internshipId) => api.get(`/student/enrollments/${internshipId}/progress`),

  // Real Project API
  selectMethod: (internshipId, method) =>
    api.post(`/student/enrollments/${internshipId}/select-method`, { method }),
  getRealProject: (internshipId) =>
    api.get(`/student/internships/${internshipId}/project`),
  submitRealProject: (formData) =>
    api.post('/student/submissions/real-project', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  requestMethodChange: (data) =>
    api.post('/student/method-change-request', data),

  // Quiz API
  quizAPI: {
    getQuestions: (internshipId) => api.get(`/quiz/internship/${internshipId}/questions`),
    startAttempt: (internshipId) => api.post(`/quiz/start/${internshipId}`),
    submitAnswer: (attemptId, questionId, data) =>
      api.post('/quiz/answer', { attemptId, questionId, ...data }),
    getAttemptProgress: (attemptId) => api.get(`/quiz/attempt/${attemptId}`),
    submitQuiz: (attemptId) => api.post(`/quiz/submit/${attemptId}`),
    getResults: (internshipId) => api.get(`/quiz/results/${internshipId}`),
    getAttemptReview: (attemptId) => api.get(`/quiz/attempt/${attemptId}/review`),
  },
};

// ─── Admin ───────────────────────────────────────────────────
export const adminAPI = {
  getAnalytics: () => api.get('/admin/analytics'),
  getPendingCounts: () => api.get('/admin/pending-counts'),
  getStudents: (params) => api.get('/admin/students', { params }),
  getStudent: (id) => api.get(`/admin/students/${id}`),
  toggleStudentActive: (id) => api.patch(`/admin/students/${id}/toggle-active`),
  getInternships: (params) => api.get('/admin/internships', { params }),
  createInternship: (data) => api.post('/admin/internships', data),
  updateInternship: (id, data) => api.put(`/admin/internships/${id}`, data),
  deleteInternship: (id) => api.delete(`/admin/internships/${id}`),
  getPayments: (params) => api.get('/admin/payments', { params }),
  getCertificates: (params) => api.get('/admin/certificates', { params }),
  revokeCertificate: (certId, data) => api.patch(`/admin/certificates/${certId}/revoke`, data),
  downloadCertificate: (certId) =>
    api.get(`/admin/certificates/${certId}/download`, { responseType: 'blob' }),

  // Offer Letters
  getOfferLetters: (params) => api.get('/offer-letters/admin/all', { params }),
  downloadOfferLetter: (offerLetterId) =>
    api.get(`/offer-letters/admin/download/${offerLetterId}`, { responseType: 'blob' }),
  resendOfferLetterEmail: (offerLetterId) =>
    api.post(`/offer-letters/admin/resend/${offerLetterId}`),
  revokeOfferLetter: (offerLetterId) =>
    api.post(`/offer-letters/admin/revoke/${offerLetterId}`),
  resendCertificateEmail: (certId) =>
    api.post(`/admin/certificates/${certId}/resend-email`),
  processRefund: (paymentId, data) =>
    api.post(`/admin/payments/${paymentId}/refund`, data),
  getPaymentRefunds: (paymentId) =>
    api.get(`/admin/payments/${paymentId}/refunds`),

  // Reviews / Testimonials
  getReviews: (params) => api.get('/admin/reviews', { params }),
  approveReview: (id) => api.patch(`/admin/reviews/${id}/approve`),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`),
  toggleFeatureReview: (id) => api.patch(`/admin/reviews/${id}/feature`),

  // Quiz Admin
  getQuizAttempts: (internshipId, params) => api.get(`/quiz/admin/internship/${internshipId}/attempts`, { params }),
  importQuizCSV: (internshipId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('internshipId', internshipId);
    return api.post('/quiz/admin/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Real Project Submissions (new flow)
  getRealProjectSubmissions: (params) => api.get('/admin/project-submissions', { params }),
  reviewRealProjectSubmission: (id, data) => api.post(`/admin/project-submissions/${id}/review`, data),

  // Certification Project Specs
  importInternshipsCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/internships/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Course Content — Modules & Lessons
  getModules: (internshipId) => api.get(`/admin/internships/${internshipId}/modules`),
  createModule: (internshipId, data) => api.post(`/admin/internships/${internshipId}/modules`, data),
  updateModule: (moduleId, data) => api.put(`/admin/modules/${moduleId}`, data),
  deleteModule: (moduleId) => api.delete(`/admin/modules/${moduleId}`),
  createLesson: (moduleId, data) => api.post(`/admin/modules/${moduleId}/lessons`, data),
  updateLesson: (lessonId, data) => api.put(`/admin/lessons/${lessonId}`, data),
  deleteLesson: (lessonId) => api.delete(`/admin/lessons/${lessonId}`),
  importModulesCSV: (internshipId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/internships/${internshipId}/import-modules-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getProjectSpec: (internshipId) => api.get(`/admin/internship-projects/${internshipId}`),
  createProjectSpec: (data) => api.post('/admin/internship-projects', data),
  updateProjectSpec: (id, data) => api.put(`/admin/internship-projects/${id}`, data),
  deleteProjectSpec: (id) => api.delete(`/admin/internship-projects/${id}`),

  // Method Change Requests
  getMethodChangeRequests: (params) => api.get('/admin/method-change-requests', { params }),
  reviewMethodChangeRequest: (id, data) => api.post(`/admin/method-change-requests/${id}/review`, data),

  // FAQs
  getFAQs: (params) => api.get('/admin/faqs', { params }),
  createFAQ: (data) => api.post('/admin/faqs', data),
  updateFAQ: (id, data) => api.put(`/admin/faqs/${id}`, data),
  deleteFAQ: (id) => api.delete(`/admin/faqs/${id}`),
  toggleFAQ: (id) => api.patch(`/admin/faqs/${id}/toggle`),

  // Team Members
  getTeamMembers: (params) => api.get('/admin/team-members', { params }),
  createTeamMember: (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null) formData.append(k, v);
    });
    return api.post('/admin/team-members', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateTeamMember: (id, data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null) formData.append(k, v);
    });
    return api.put(`/admin/team-members/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTeamMember: (id) => api.delete(`/admin/team-members/${id}`),

  // Audit Log
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),

  // Email Campaigns
  getCampaigns: () => api.get('/admin/campaigns'),
  sendCampaign: (data) => api.post('/admin/campaigns/send', data),
  getCampaignRecipientCount: (target) => api.get('/admin/campaigns/recipient-count', { params: { target } }),

  // User Activity
  getUserActivity:     (params) => api.get('/admin/user-activity', { params }),
  getSupportTickets:   (params) => api.get('/admin/support-tickets', { params }),
  updateTicketStatus:  (id, data) => api.patch(`/admin/support-tickets/${id}/status`, data),

  // Employees
  getEmployees: (params) => api.get('/admin/employees', { params }),
  getEmployeeStats: () => api.get('/admin/employees/stats'),
  getEmployee: (id) => api.get(`/admin/employees/${id}`),
  createEmployee: (data) => api.post('/admin/employees', data),
  updateEmployee: (id, data) => api.put(`/admin/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/admin/employees/${id}`),
  suspendEmployee: (id) => api.post(`/admin/employees/${id}/suspend`),
  activateEmployee: (id) => api.post(`/admin/employees/${id}/activate`),
  resetEmployeePassword: (id) => api.post(`/admin/employees/${id}/reset-password`),
  getEmployeeRoles: () => api.get('/admin/employee-meta/roles'),
  getEmployeeDepartments: () => api.get('/admin/employee-meta/departments'),

  // Tasks
  getTasks: (params) => api.get('/admin/tasks', { params }),
  createTask: (data) => api.post('/admin/tasks', data),
  updateTask: (id, data) => api.put(`/admin/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/admin/tasks/${id}`),

  // Announcements
  getAnnouncements: (params) => api.get('/admin/announcements', { params }),
  createAnnouncement: (data) => api.post('/admin/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),

  // CSV Export
  exportStudents: () => api.get('/export/students', { responseType: 'blob' }),
  exportEnrollments: () => api.get('/export/enrollments', { responseType: 'blob' }),
  exportPayments: () => api.get('/export/payments', { responseType: 'blob' }),
  exportCertificates: () => api.get('/export/certificates', { responseType: 'blob' }),
  exportInternships: () => api.get('/export/internships', { responseType: 'blob' }),
  exportAll: () => api.get('/export/all', { responseType: 'blob' }),

  // Analytics API (for BI tools)
  getAnalyticsDashboard: () => api.get('/analytics/dashboard'),
  getAnalyticsRevenue: () => api.get('/analytics/revenue-monthly'),
  getAnalyticsEnrollments: () => api.get('/analytics/enrollment-by-internship'),
  getAnalyticsStudentGrowth: () => api.get('/analytics/student-growth'),
  getAnalyticsCategories: () => api.get('/analytics/category-performance'),
  getAnalyticsQuiz: () => api.get('/analytics/quiz-performance'),
  getAnalyticsGeography: () => api.get('/analytics/geography'),
  getAnalyticsReferrals: () => api.get('/analytics/referral-performance'),

  // Python Analytics
  getPythonManifest: () => api.get('/python-analytics/manifest'),
  getPythonInsights: () => api.get('/python-analytics/insights'),
  getPythonReports: () => api.get('/python-analytics/reports'),
  generatePythonAnalytics: () => api.post('/python-analytics/generate'),
  getPythonChartUrl: (name) => `${API_URL}/python-analytics/chart/${name}`,
  getPythonChartRaw: (name) => api.get(`/python-analytics/chart/${name}`, { responseType: 'blob' }),
};

export const publicAPI = {
  getTeamMembers: () => api.get('/team-members'),
};

// ─── Referral ────────────────────────────────────────────────
export const referralAPI = {
  validate: (code) => api.post('/referrals/validate', { code }),
  getMyInfo: () => api.get('/referrals/my/referral-info'),
  getMyFriends: () => api.get('/referrals/my/friends'),
  getRewardTiers: () => api.get('/referrals/reward-tiers'),
  admin: {
    getList: (params) => api.get('/referrals/admin/list', { params }),
    getDetail: (id) => api.get(`/referrals/admin/${id}`),
    getDashboardStats: () => api.get('/referrals/admin/dashboard-stats'),
    getRewardTiers: () => api.get('/referrals/admin/reward-tiers'),
    createRewardTier: (data) => api.post('/referrals/admin/reward-tiers', data),
    updateRewardTier: (id, data) => api.put(`/referrals/admin/reward-tiers/${id}`, data),
    deleteRewardTier: (id) => api.delete(`/referrals/admin/reward-tiers/${id}`),
  },
};

// ─── Coupon ──────────────────────────────────────────────────
export const couponAPI = {
  validate: (code, internshipId) => api.post('/coupons/validate', { code, internshipId }),
  getActive: (internshipId) => api.get('/coupons/active', { params: { internshipId } }),
  admin: {
    getList: (params) => api.get('/coupons/admin/list', { params }),
    getDetail: (id) => api.get(`/coupons/admin/${id}`),
    create: (data) => api.post('/coupons/admin/create', data),
    update: (id, data) => api.put(`/coupons/admin/${id}`, data),
    delete: (id) => api.delete(`/coupons/admin/${id}`),
    toggle: (id) => api.post(`/coupons/admin/${id}/toggle`),
    duplicate: (id) => api.post(`/coupons/admin/${id}/duplicate`),
    getStats: () => api.get('/coupons/admin/stats'),
  },
};

// ─── Wallet ──────────────────────────────────────────────────
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (params) => api.get('/wallet/transactions', { params }),
  admin: {
    getUsers: (params) => api.get('/wallet/admin/users', { params }),
    getUserDetail: (userId) => api.get(`/wallet/admin/users/${userId}`),
    getUserTransactions: (userId, params) => api.get(`/wallet/admin/users/${userId}/transactions`, { params }),
    credit: (userId, amount, reason) => api.post('/wallet/admin/credit', { userId, amount, reason }),
    debit: (userId, amount, reason) => api.post('/wallet/admin/debit', { userId, amount, reason }),
    freeze: (userId) => api.post('/wallet/admin/freeze', { userId }),
    getStats: () => api.get('/wallet/admin/stats'),
  },
};

export const reviewAPI = {
  getAll: (params) => api.get('/reviews', { params }),
  submit: (data) => api.post('/reviews', data),
};

export const faqAPI = {
  getAll: (params) => api.get('/faqs', { params }),
};

// ─── Employee Portal ────────────────────────────────────────
export const employeeAPI = {
  login: (data) => api.post('/employee/auth/login', data),
  logout: () => api.post('/employee/auth/logout'),
  getMe: () => api.get('/employee/auth/me'),
  updateProfile: (data) => api.put('/employee/auth/profile', data),
  changePassword: (data) => api.put('/employee/auth/change-password', data),
  forceChangePassword: (data) => api.put('/employee/auth/force-change-password', data),
  getDashboardStats: () => api.get('/employee/dashboard/stats'),
  getEmployees: (params) => api.get('/employee', { params }),
  getEmployee: (id) => api.get(`/employee/${id}`),
  createEmployee: (data) => api.post('/employee', data),
  updateEmployee: (id, data) => api.put(`/employee/${id}`, data),
  deleteEmployee: (id) => api.delete(`/employee/${id}`),
  suspendEmployee: (id) => api.post(`/employee/${id}/suspend`),
  activateEmployee: (id) => api.post(`/employee/${id}/activate`),
  resetPassword: (id, data) => api.post(`/employee/${id}/reset-password`, data),
  getMyTasks: (params) => api.get('/employee/tasks/my', { params }),
  getAllTasks: (params) => api.get('/employee/tasks/all', { params }),
  getTaskStats: () => api.get('/employee/tasks/stats'),
  getTask: (id) => api.get(`/employee/tasks/${id}`),
  createTask: (data) => api.post('/employee/tasks', data),
  updateTask: (id, data) => api.put(`/employee/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/employee/tasks/${id}`),
  addTaskComment: (id, data) => api.post(`/employee/tasks/${id}/comments`, data),
  getNotifications: (params) => api.get('/employee/notifications', { params }),
  getUnreadCount: () => api.get('/employee/notifications/unread-count'),
  markNotificationRead: (id) => api.put(`/employee/notifications/${id}/read`),
  markAllRead: () => api.put('/employee/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/employee/notifications/${id}`),
  getRoles: () => api.get('/employee/meta/roles'),
  getDepartments: () => api.get('/employee/meta/departments'),
};

export const helpAPI = {
  submitTicket: (formData) => api.post('/help/ticket', formData),
};

// ─── Gamification / XP ─────────────────────────────────────────
export const gamificationAPI = {
  getStats:      () => api.get('/gamification/stats'),
  getStreak:     () => api.get('/gamification/streak'),
  getBadges:     () => api.get('/gamification/badges'),
  getLeaderboard:(params) => api.get('/gamification/leaderboard', { params }),
  getXpLeaderboard:    (params) => api.get('/gamification/leaderboard/xp', { params }),
  getStreakLeaderboard:(params) => api.get('/gamification/leaderboard/streak', { params }),
  getInternshipLeaderboard: (internshipId, params) => api.get(`/gamification/leaderboard/internship/${internshipId}`, { params }),
  earnXp:        (data) => api.post('/gamification/earn-xp', data),
};

export default api;